/**
 * «Редактор» — одно рабочее место управляющего вместо двух разделов.
 *
 * Было до 12.09.2026: «Наполнение» (карточки блюд) и «База знаний»
 * (регламенты, вопросы без ответа) стояли в общем ряду с разделами
 * сотрудника. На телефоне шесть кнопок не вмещались в строку, ряд
 * переносился, и владелец сказал прямо: разделы выглядят дублирующими
 * друг друга. Они и правда парные — «Наполнение» это изнанка
 * «Справочника», «База знаний» — изнанка «Помощника», — но парность была
 * видна только тому, кто знает устройство.
 *
 * Стало: один вход (шестерёнка в шапке) и три вкладки внутри — «Блюда»,
 * «Курсы», «Регламенты». Сотрудник в меню видит четыре раздела вместо
 * шести, а управляющий — одно место, где заводят содержимое.
 *
 * **Вкладки показываются по правам, а не по уровню** (D181): «Блюда ·
 * править», «Курсы · править», «Помощник · править» выдаются по
 * отдельности, и человек с одним правом видит одну вкладку. Решает
 * сервер, приложение читает его ответ — спрятанная вкладка защитой не
 * является.
 */

import { useState } from 'react';
import { Button } from '@shell/ui';
import { Admin } from './Admin';
import { EditorCourses } from './EditorCourses';
import { EditorMicro } from './EditorMicro';
import { EditorScenarios } from './EditorScenarios';
import { Knowledge } from './Knowledge';
import { Reports } from './Reports';

export interface EditorRights {
  dishes: boolean;
  courses: boolean;
  knowledge: boolean;
  /** «Отчёты по обучению» — своя галочка (D235). */
  reports?: boolean;
  /** Уровень `admin` или владелец — панель наставника `S7`. */
  manager?: boolean;
  /** Владелец системы — сводка собственнику `S8`. */
  owner?: boolean;
}

export function Editor({ rights }: { rights: EditorRights }) {
  const tabs = [
    ...(rights.dishes ? [{ code: 'dishes', title: 'Блюда' }] : []),
    ...(rights.courses ? [{ code: 'courses', title: 'Курсы' }] : []),
    ...(rights.knowledge ? [{ code: 'knowledge', title: 'Регламенты' }] : []),
    // Сценарии тренировок — учебное содержимое, и правит их тот же, кто
    // правит курсы (S10): отдельного права ради шестнадцати текстов нет.
    ...(rights.courses ? [{ code: 'scenarios', title: 'Тренировки' }] : []),
    ...(rights.courses ? [{ code: 'micro', title: 'Микроуроки' }] : []),
    // Отчёты §12.2: каждый внутри открывается своим правом (`Reports.tsx`).
    ...(rights.reports || rights.manager
      ? [{ code: 'reports', title: 'Отчёты' }]
      : []),
  ];
  const [tab, setTab] = useState(tabs[0]?.code ?? 'dishes');
  const current = tabs.some((item) => item.code === tab) ? tab : (tabs[0]?.code ?? 'dishes');

  return (
    <>
      {tabs.length > 1 && (
        <div className="editor-tabs">
          {tabs.map((item) => (
            <Button
              key={item.code}
              small
              variant={current === item.code ? 'primary' : 'quiet'}
              onClick={() => setTab(item.code)}
            >
              {item.title}
            </Button>
          ))}
        </div>
      )}

      {current === 'dishes' && <Admin />}
      {current === 'courses' && <EditorCourses />}
      {current === 'knowledge' && <Knowledge />}
      {current === 'scenarios' && <EditorScenarios />}
      {current === 'micro' && <EditorMicro />}
      {current === 'reports' && <Reports rights={rights} />}
    </>
  );
}
